import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { Brain, Zap, ShieldAlert } from 'lucide-react';

export function NeuralPath() {
    const [path, setPath] = useState<number[]>([]);
    const [userPath, setUserPath] = useState<number[]>([]);
    const [gameState, setGameState] = useState<'idle' | 'showing' | 'playing' | 'failed' | 'success'>('idle');
    const [level, setLevel] = useState(1);
    const [highScore, setHighScore] = useState(0);

    const gridSize = 9; // 3x3 grid

    const generatePath = useCallback((currentLevel: number) => {
        const newPath = Array.from({ length: currentLevel + 2 }, () => Math.floor(Math.random() * gridSize));
        setPath(newPath);
        setGameState('showing');
    }, []);

    const startGame = () => {
        setLevel(1);
        setUserPath([]);
        generatePath(1);
    };

    useEffect(() => {
        if (gameState === 'showing') {
            const timer = setTimeout(() => {
                setGameState('playing');
                setUserPath([]);
            }, (path.length * 800) + 500);
            return () => clearTimeout(timer);
        }
    }, [gameState, path]);

    const handleCellClick = (index: number) => {
        if (gameState !== 'playing') return;

        const newUserPath = [...userPath, index];
        setUserPath(newUserPath);

        // Check if correct
        const currentIndex = newUserPath.length - 1;
        if (index !== path[currentIndex]) {
            setGameState('failed');
            if (level > highScore) setHighScore(level);
            return;
        }

        // Check if level complete
        if (newUserPath.length === path.length) {
            setGameState('success');
            setTimeout(() => {
                setLevel(prev => prev + 1);
                generatePath(level + 1);
            }, 1000);
        }
    };

    return (
        <GlassCard className="p-8 w-full max-w-md border-emerald-500/20 bg-black/60 backdrop-blur-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />

            <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-2">
                    <Brain className="text-emerald-500 w-5 h-5" />
                    <span className="text-xs font-mono text-emerald-500 uppercase tracking-widest">Neural Path</span>
                </div>
                <div className="flex gap-4 font-mono text-[10px] text-gray-400">
                    <span>LVL: {level}</span>
                    <span>BEST: {highScore}</span>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-8">
                {Array.from({ length: gridSize }).map((_, i) => (
                    <motion.button
                        key={i}
                        whileHover={gameState === 'playing' ? { scale: 1.05 } : {}}
                        whileTap={gameState === 'playing' ? { scale: 0.95 } : {}}
                        onClick={() => handleCellClick(i)}
                        className={`aspect-square rounded-lg border flex items-center justify-center transition-all duration-300 ${gameState === 'showing' && path[userPath.length] === i
                                ? 'bg-emerald-500/50 border-emerald-400 shadow-[0_0_20px_rgba(52,211,153,0.4)]'
                                : 'bg-white/5 border-white/10 hover:border-emerald-500/30'
                            }`}
                        animate={
                            gameState === 'showing' && path[Math.floor(Date.now() / 800) % path.length] === i && gameState === 'showing'
                                ? { backgroundColor: 'rgba(16, 185, 129, 0.4)' }
                                : {}
                        }
                    >
                        <div className={`w-2 h-2 rounded-full ${userPath.includes(i) ? 'bg-emerald-500' : 'bg-transparent'}`} />
                    </motion.button>
                ))}
            </div>

            <div className="text-center">
                <AnimatePresence mode="wait">
                    {gameState === 'idle' && (
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={startGame}
                            className="px-8 py-3 bg-emerald-500 text-black text-xs font-black uppercase tracking-[0.3em] rounded-full shadow-lg shadow-emerald-500/20"
                        >
                            Initialize Sync
                        </motion.button>
                    )}

                    {gameState === 'showing' && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-xs font-mono text-emerald-400 animate-pulse"
                        >
                            RECORDING_NEURAL_SEQUENCE...
                        </motion.div>
                    )}

                    {gameState === 'playing' && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-xs font-mono text-white"
                        >
                            INPUT_REQUIRED: [{userPath.length}/{path.length}]
                        </motion.div>
                    )}

                    {gameState === 'failed' && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="space-y-4"
                        >
                            <div className="flex items-center justify-center gap-2 text-red-500">
                                <ShieldAlert size={16} />
                                <span className="text-xs font-mono uppercase">Sync Severed</span>
                            </div>
                            <button
                                onClick={startGame}
                                className="text-[10px] text-emerald-500 hover:text-white transition-colors uppercase font-mono tracking-widest"
                            >
                                Re-Link System
                            </button>
                        </motion.div>
                    )}

                    {gameState === 'success' && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex items-center justify-center gap-2 text-emerald-500"
                        >
                            <Zap size={16} className="animate-bounce" />
                            <span className="text-xs font-mono uppercase">Path Synchronized</span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </GlassCard>
    );
}
